import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import AffiliateNotice from "../../components/AffiliateNotice";
import { api, errorMessage } from "./api";
import { AffiliateDiscountCode } from "./types";

export default function AffiliateDiscountCodes({ affiliateId, csrf, editable, eligible }: {
  affiliateId: string; csrf: string; editable: boolean; eligible: boolean;
}) {
  const [codes, setCodes] = useState<AffiliateDiscountCode[]>([]);
  const [code, setCode] = useState("");
  const [percentage, setPercentage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [reload, setReload] = useState(0);
  const submitting = useRef(false);
  useEffect(() => {
    let current = true;
    setLoaded(false); setError("");
    api.affiliateDiscountCodes(affiliateId, csrf).then(result => {
      if (current) { setCodes(result); setLoaded(true); }
    }).catch(reason => { if (current) setError(errorMessage(reason)); });
    return () => { current = false; };
  }, [affiliateId, csrf, reload]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setError(""); setMessage("");
    try {
      const result = await api.assignAffiliateDiscount(affiliateId, code.trim().toUpperCase(), percentage, csrf);
      setCodes(previous => [result, ...previous.filter(item => item.id !== result.id)]);
      setMessage(`Discount code ${result.code} assigned.`); setCode(""); setPercentage("");
    } catch (reason) { setError(errorMessage(reason)); }
    finally { submitting.current = false; setBusy(false); }
  };
  return <Stack spacing={2}>
    <Typography component="h3" variant="h6">Affiliate discount codes</Typography>
    <Typography color="text.secondary">The redeemed code takes priority over referral links. Each paid purchase counts once. Commission is deducted after the discount and before partner splits.</Typography>
    {error && <AffiliateNotice severity="error">{error}{!loaded && <Button onClick={() => setReload(value => value + 1)}>Reload codes</Button>}</AffiliateNotice>}
    {message && <AffiliateNotice severity="success" role="status">{message}</AffiliateNotice>}
    {codes.map(item => <Typography key={item.id}>{item.code} · {Number(item.percentage)}% off{!item.activated_at && " · Setup incomplete — retry the same name and amount"}</Typography>)}
    {loaded && codes.length === 0 && <Typography color="text.secondary">No discount codes assigned.</Typography>}
    {editable && <Box component="form" onSubmit={submit}>
      <Stack spacing={2}>
        <TextField label="Code name" value={code} onChange={event => setCode(event.target.value)} required disabled={busy || !eligible}
          inputProps={{ maxLength: 40, pattern: "[A-Za-z0-9]+" }} helperText="Letters and numbers only; customers enter this code at checkout." />
        <TextField label="Discount (%)" type="number" value={percentage} onChange={event => setPercentage(event.target.value)} required disabled={busy || !eligible}
          inputProps={{ min: 0.01, max: 100, step: 0.01 }} />
        <Button type="submit" disabled={busy || !eligible || !loaded}>Assign discount code</Button>
        {!eligible && <Typography color="text.secondary">Activate the verified affiliate to assign a discount code.</Typography>}
      </Stack>
    </Box>}
  </Stack>;
}
